import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface SimpleTourProps {
  isOpen: boolean;
  onClose: () => void;
}

const tourSteps = [
  {
    selector: '[data-tour="filter-panel"]',
    title: 'Welcome to Apply.AI!',
    content: 'Use our advanced database to find your perfect college fit. Let\'s start by exploring our college search filters.',
    page: '/college-list-builder',
  },
  {
    selector: '[data-tour="heart-icon"]',
    title: 'Add Colleges to Your List',
    content: 'Click the heart icon to add colleges to your personal list. This helps you keep track of schools you\'re interested in.',
    page: '/college-list-builder',
  },
  {
    selector: '[data-tour="admissions-stats"]',
    title: 'Compare Your Stats',
    content: 'View admission statistics like SAT scores, ACT scores, and acceptance rates to see how you compare to other applicants.',
    page: '/college-list-builder',
  },
  {
    selector: '[data-tour="essay-coach-dropdown"]',
    title: 'Get Essay Feedback',
    content: 'Use the Essay Coach to select your college and get AI-powered feedback on your application materials.',
    page: '/essay-coach',
  },
  {
    selector: '[data-tour="common-app"]',
    title: 'Personal Statement',
    content: 'Select your application type (Common App, Coalition App, etc.) to start working on your personal statement.',
    page: '/analyze/1',
  },
  {
    selector: '[data-tour="supplements"]',
    title: 'Supplemental Essays',
    content: 'Or select any supplement essay to get started! Each college has specific supplemental requirements.',
    page: '/analyze/1',
  },
  {
    selector: '[data-tour="essay-writing-area"]',
    title: 'Analyze Your Essays',
    content: 'Simply paste your essay and click analyze, or type "I\'m stuck and need ideas" to get inspired. Our AI will provide detailed feedback!',
    page: '/analyze/1',
  },
  {
    selector: '[data-tour="text-notifications"]',
    title: 'Text Notifications:',
    content: 'Sign up for text updates to stay on top of university deadlines and get updated when a decision is released on your application.',
    page: '/analyze/1',
  },
  {
    selector: '[data-tour="application-hub"]',
    title: 'Application Hub',
    content: 'Here you can find an overview of the colleges you are applying to and the essays you have started working on.',
    page: '/',
  },
];

const SimpleTour: React.FC<SimpleTourProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [foundElement, setFoundElement] = useState<Element | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setIsReady(false);
    let retryCount = 0;
    const maxRetries = 30;

    const updatePosition = () => {
      const currentTourStep = tourSteps[currentStep];
      let element = document.querySelector(currentTourStep.selector);      
      const isLoadingPlaceholder = element && (
        element.textContent?.includes('Loading university data...') ||
        element.textContent?.includes('Loading universities...') ||
        element.textContent?.includes('Loading tour...')
      );
      
      if (!element) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(updatePosition, 500);
        } else {
          setIsReady(false);
        }
        return;
      }
      
      if (isLoadingPlaceholder) {
        retryCount++;
        if (retryCount < maxRetries - 5) {
          setTimeout(updatePosition, 800);
        } else {
          // Fallback: proceed with loading element if we've waited long enough
          // Continue with the positioning logic below
        }
        return;
      }
      setFoundElement(element);
      
      const popoverWidth = 400;
      const popoverHeight = 250;
      
      if (currentStep === 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!isVisible) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      setTimeout(() => {
        if (!element) return;
        const newRect = element.getBoundingClientRect();
        
        let finalTop = newRect.top + (newRect.height - popoverHeight) / 2;
        let finalLeft = newRect.right + 20;
        
        if (finalLeft + popoverWidth > window.innerWidth) {
          finalLeft = newRect.left - popoverWidth - 20;
          
          if (finalLeft < 20) {
            finalTop = newRect.bottom + 15;
            finalLeft = Math.min(newRect.left, window.innerWidth - popoverWidth - 20);

            finalLeft = Math.max(20, finalLeft);
            if (finalTop + popoverHeight > window.innerHeight) {
              finalTop = newRect.top - popoverHeight - 15;
              
              if (finalTop < 20) {
                finalTop = Math.max(20, (window.innerHeight - popoverHeight) / 2);
                finalLeft = Math.max(20, (window.innerWidth - popoverWidth) / 2);
              }
            }
          }
        }
        
        // Ensure popover stays within vertical bounds when positioned to the side
        if (finalTop < 20) {
          finalTop = 20;
        }
        if (finalTop + popoverHeight > window.innerHeight) {
          finalTop = window.innerHeight - popoverHeight - 20;
        }
        
        setPopoverPosition({ top: finalTop, left: finalLeft });
        setIsReady(true);
      }, 300);
    };

    const currentTourStepData = tourSteps[currentStep];
    const needsLongerDelay = currentTourStepData?.page === '/essay-coach' || currentTourStepData?.page === '/analyze/1';
    const initialDelay = needsLongerDelay ? 1500 : 800;
    const timeoutId = setTimeout(updatePosition, initialDelay);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen, currentStep]);

  if (!isOpen) {
    return null;
  }

  if (!isReady || !foundElement) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" style={{ zIndex: 99999 }}>
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--surface-primary)' }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p>Loading tour...</p>
        </div>
      </div>
    );
  }

  const currentTourStep = tourSteps[currentStep];
  const targetElement = foundElement;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      const nextTourStep = tourSteps[nextStep];
      setCurrentStep(nextStep);
      setIsReady(false);
      
      if (nextTourStep.page && window.location.pathname !== nextTourStep.page) {
        navigate(nextTourStep.page);
      }
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const prevTourStep = tourSteps[prevStep];
      setCurrentStep(prevStep);
      setIsReady(false);
      
      if (prevTourStep.page && window.location.pathname !== prevTourStep.page) {
        navigate(prevTourStep.page);
      }
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const targetRect = targetElement.getBoundingClientRect();

  const headerHeight = 80;
  const footerHeight = 60;

  return (
    <>
      <div 
        className="fixed bg-black bg-opacity-60 z-50"
        style={{ 
          zIndex: 99999,
          top: headerHeight,
          left: 0,
          right: 0,
          height: Math.max(0, targetRect.top - 8 - headerHeight)
        }}
      />
      
      <div 
        className="fixed bg-black bg-opacity-60 z-50"
        style={{ 
          zIndex: 99999,
          top: Math.max(headerHeight, targetRect.top - 8),
          left: 0,
          width: Math.max(0, targetRect.left - 8),
          height: targetRect.height + 16
        }}
      />
      
      <div 
        className="fixed bg-black bg-opacity-60 z-50"
        style={{ 
          zIndex: 99999,
          top: Math.max(headerHeight, targetRect.top - 8),
          left: targetRect.right + 8,
          right: 0,
          height: targetRect.height + 16
        }}
      />
      
      <div 
        className="fixed bg-black bg-opacity-60 z-50"
        style={{ 
          zIndex: 99999,
          top: targetRect.bottom + 8,
          left: 0,
          right: 0,
          bottom: footerHeight
        }}
      />
      
      <div
        className="fixed pointer-events-none border-2 border-white rounded-lg z-50"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          zIndex: 100000,
        }}
      />
      
      <div
        className="fixed border-4 border-blue-500 rounded-lg pointer-events-none z-50 animate-pulse"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          zIndex: 100001,
          boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3)',
        }}
      />
      
      <div
        className="fixed rounded-xl shadow-2xl p-6 z-50 border"
        style={{
          background: 'var(--surface-primary)',
          borderColor: 'var(--border-primary)',
          top: popoverPosition.top,
          left: popoverPosition.left,
          maxWidth: 400,
          minWidth: 350,
          zIndex: 100002,
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="bg-blue-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
            {currentStep + 1} / {tourSteps.length}
          </div>
          <button
            onClick={handleSkip}
            className="text-sm hover:underline" style={{ color: 'var(--text-tertiary)' }}
          >
            Skip Tour
          </button>
        </div>
        
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{currentTourStep.title}</h3>
        <p className="mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{currentTourStep.content}</p>
        
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-4 py-2 rounded-md transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'var(--surface-hover)', color: 'var(--text-primary)' }}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            {currentStep === tourSteps.length - 1 ? 'Finish Tour' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
};

export default SimpleTour;